import { z } from 'zod';
import { RoleGrantSchema, RoleGrantWriteSchema } from './user.js';

// Anlegen: Mail + Name + intern/extern; optional gleich eine erste Rolle auf dieses Projekt.
export const CreateUserInputSchema = z.object({
  email: z
    .string()
    .email()
    .transform((s) => s.trim().toLowerCase()),
  name: z.string().min(1),
  is_external: z.boolean(),
  // RoleGrant, nicht RoleAssignment: das Projekt steht in der URL, nicht im Body (#23).
  initial_role: RoleGrantWriteSchema.optional(),
});
export type CreateUserInput = z.infer<typeof CreateUserInputSchema>;

// POST /users/:userId/replace-stub — Platzhalter (is_stub) in einem Schritt zum echten, aktiven
// Nutzer mit Rolle machen. Die Rolle ist Pflicht (ein aktiver Nutzer ohne Rolle wäre wieder ein
// Zwischenzustand), das Projekt steht in der URL (RoleGrant, nicht RoleAssignment — #23).
// Eine Platzhalter-Adresse als „echte" Mail würde den Nutzer weiter als Stub ausweisen.
export const StubReplaceInputSchema = z.object({
  email: z
    .string()
    .email()
    .transform((s) => s.trim().toLowerCase())
    .refine((e) => !e.endsWith('@stub.invalid'), { message: 'stub_email_not_allowed' }),
  name: z.string().min(1),
  is_external: z.boolean(),
  role: RoleGrantWriteSchema,
});
export type StubReplaceInput = z.infer<typeof StubReplaceInputSchema>;

// POST /users/:userId/merge-into — Platzhalter in einen BESTEHENDEN Nutzer zusammenführen: alle
// Verweise wandern zum Ziel, der Platzhalter wird gelöscht (unumkehrbar).
export const StubMergeInputSchema = z.object({
  target_user_id: z.string().uuid(),
});
export type StubMergeInput = z.infer<typeof StubMergeInputSchema>;

// Rolle hinzufügen ODER entfernen (mindestens eins). scope_node_id per RoleGrant (uuid|null);
// das Projekt kommt aus der URL (#23).
export const RoleChangeInputSchema = z
  .object({
    add: RoleGrantWriteSchema.optional(),
    remove: RoleGrantSchema.optional(),
  })
  .refine((v) => v.add !== undefined || v.remove !== undefined, {
    message: 'add_or_remove_required',
  });
export type RoleChangeInput = z.infer<typeof RoleChangeInputSchema>;

// PATCH /users/:userId — is_active umschalten und/oder E-Mail/Name pflegen (mind. eins).
// Ein Feld statt drei getrennter Routen, weil `PATCH /users/:userId` bereits für is_active
// vergeben ist — eine zweite Route auf demselben Pfad würde in Hono kollidieren.
export const UserUpdateInputSchema = z
  .object({
    is_active: z.boolean().optional(),
    email: z
      .string()
      .email()
      .transform((s) => s.trim().toLowerCase())
      .optional(),
    name: z.string().min(1).optional(),
  })
  .refine((v) => v.is_active !== undefined || v.email !== undefined || v.name !== undefined, {
    message: 'update_field_required',
  });
export type UserUpdateInput = z.infer<typeof UserUpdateInputSchema>;

// Read-Modelle (Server liefert IDs + optional Knotennamen für die Scope-Chips).
export interface ProjectUserRole {
  user_role_id: string;
  role: string;
  scope_node_id: string | null;
  scope_node_name: string | null;
}
export interface ProjectUser {
  user_id: string;
  email: string;
  name: string;
  is_external: boolean;
  is_active: boolean;
  is_stub: boolean;
  roles: ProjectUserRole[];
}
export interface UnassignedUser {
  user_id: string;
  email: string;
  name: string;
  is_external: boolean;
}
